import { Component, FunctionalComponent } from 'preact'
import Phrase from '../../libs/translationsUtils/phrase'

import Popup from '../Popup/index'
import Tabs from '../Tabs/index'
import UserAvatar from '../User/UserAvatar'
import UserName from '../User/UserName'
import ReputationBar from '../User/ReputationBar'

import getWithInMemoryCache from '../../utils/getWithInMemoryCache'
import globals from '../../utils/globals'

interface UserStatisticProps {
  label: string
  value: number
}

export const UserStatistic: FunctionalComponent<UserStatisticProps> = ({ label, value }) => (
  <div>
    <div>{value}</div>
    <div className='text--meta'>
      <Phrase p={label} count={{ value: String(value) }} />
    </div>
  </div>
)

const SendMessageIcon = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M2 0C.898 0 0 .898 0 2v12c0 1.102.898 2 2 2h6.594c-.102-.3-.094-.7-.094-1 0-.3-.008-.7.094-1H2V2h16v7.188c.8.398 1.398.925 2 1.624V2c0-1.102-.898-2-2-2H2zm1 2.594v2L10 9l7-4.406v-2L10 7 3 2.594zM15 10c-2.8 0-5 2.2-5 5s2.2 5 5 5 5-2.2 5-5-2.2-5-5-5zm-1 2h2v2h2v2h-2v2h-2v-2h-2v-2h2v-2z'
      fill-rule='nonzero'
      fill='#000'
    />
  </svg>
)

interface UserProfileField {
  value: string
  values: string[]
  name: string
}

interface User {
  avatar: string
  numberOfTopics: number
  numberOfReplies: number
  numberOfSolved: number
  showBadges: boolean
  showReputation: boolean
  name: string
  userTitle: string
  id: number
  rank: null
  badges: Array<{
    title: string
    url: string
  }>
  username: string
  profileFields: UserProfileField[]
}

interface UserStatsProp {
  user: User
}

export const UserStats = ({ user }: UserStatsProp) => (
  <div
    style={{
      display: 'flex',
      width: '100%',
      justifyContent: 'space-between'
    }}
  >
    {[
      { value: user.numberOfTopics, label: 'Topic|Topics' },
      { value: user.numberOfReplies, label: 'Reply|Replies' },
      { value: user.numberOfSolved, label: 'Solved' }
    ].map((statistic, index) => (
      <UserStatistic key={index} {...statistic} />
    ))}
  </div>
)

interface ExtraUserInfoProps extends UserStatsProp {
  disableTabs: boolean | undefined
  user: User
}

const ExtraUserInfo = ({ user, disableTabs }: ExtraUserInfoProps) => {
  if (user.showBadges) {
    return (
      <Tabs
        titles={[<Phrase key={1} p='Badges' />, <Phrase key={2} p='About' />]}
        style={{ padding: '20px 0' }}
        disableClick={disableTabs}
      >
        <BadgeList user={user} />
        <ProfileFields profileFields={user.profileFields} />
      </Tabs>
    )
  }
  return (
    <div style={{ paddingTop: '20px' }}>
      <ProfileFields profileFields={user.profileFields} />
    </div>
  )
}

interface UserProfileProps {
  user: User
  profileRef: (ref: HTMLElement | null) => void
  currentUserId: number
  disableTabs: boolean | undefined
  isPMActive: boolean
  isReputationActive: boolean
}

export const UserProfile = ({
  profileRef,
  user,
  currentUserId,
  disableTabs,
  isPMActive,
  isReputationActive
}: UserProfileProps) => (
  <div>
    <div
      ref={(el) => profileRef(el)}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'top',
        width: '100%'
      }}
    >
      <div>
        <UserAvatar
          disablePopup
          showRank
          user={user}
          style={{
            marginBottom: '10px',
            width: '60px',
            height: '60px'
          }}
        />
        {isReputationActive && user.showReputation && <ReputationBar user={user} />}
      </div>

      <div
        style={{
          flexGrow: 1,
          marginLeft: '20px'
        }}
      >
        <UserName user={user} />
        <div
          className='text--meta'
          style={{
            marginBottom: '10px'
          }}
        >
          {user.userTitle}
        </div>
        <UserStats user={user} />
      </div>

      {Boolean(isPMActive && currentUserId && currentUserId !== user.id) && (
        <a
          href={
            globals('application') !== 'control'
              ? `/inbox/conversation?with=${user.id}`
              : `/privateMessages/overview#conversation/${user.id}`
          }
        >
          <SendMessageIcon style={{ position: 'absolute', top: '20px', right: '20px' }} />
        </a>
      )}
    </div>
    <ExtraUserInfo user={user} disableTabs={disableTabs} />
  </div>
)

interface BadgeListProps {
  user: User
}

const BadgeList = ({ user }: BadgeListProps) => {
  const { badges, name } = user
  if (badges.length) {
    return (
      <div
        style={{
          display: 'flex',
          width: '100%',
          alignContent: 'space-around',
          flexWrap: 'wrap'
        }}
      >
        {badges.map(({ title, url }, index) => (
          <div
            key={index}
            style={{
              float: 'left',
              display: 'flex',
              alignItems: 'center',
              width: '50%',
              paddingRight: index % 2 === 1 ? '5px' : 0,
              paddingTop: index >= 2 ? '8px' : 0
            }}
          >
            <img
              title={title}
              src={url}
              style={{
                width: '32px',
                marginRight: '8px'
              }}
            />
            <div
              style={{
                maxWidth: 'calc(100% - 40px)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {title}
            </div>
          </div>
        ))}
      </div>
    )
  }
  return <Phrase p='member.profile.no.badges' m='Common' count={{ username: name }} />
}

const TableRow: FunctionalComponent<UserProfileField> = ({ value, values, name }) => {
  return (
    <tr style={{ wordWrap: 'break-word' }}>
      <td style={{ width: '30%', verticalAlign: 'top' }}>{name}</td>
      <td
        style={{
          textAlign: 'right',
          width: '70%',
          paddingLeft: '10px',
          verticalAlign: 'top'
        }}
      >
        {value || values.join(', ')}
      </td>
    </tr>
  )
}

const ProfileFields: FunctionalComponent<{
  profileFields: UserProfileField[]
}> = ({ profileFields }) => {
  if (!profileFields) return null

  return (
    <table
      style={{
        borderCollapse: 'collapse',
        width: '100%',
        tableLayout: 'fixed'
      }}
    >
      <tbody>
        {profileFields
          .filter(({ value, values }) => value || values?.length)
          .map((profileField, index) => (
            <TableRow key={index} {...profileField} />
          ))}
      </tbody>
    </table>
  )
}

const loadMemberProfile = (memberId: number, cb: (u: User) => void, liveData?: boolean) => {
  if (liveData) {
    getWithInMemoryCache.clearCache()
  }
  getWithInMemoryCache('/member/memberProfile', { memberId }).then(cb)
}

interface UserProfileApiWrapperProps {
  liveData?: boolean
  memberId: number
  isPMActive: boolean
  isReputationActive: boolean
}

interface State {
  user: User
}

export class UserProfileApiWrapper extends Component<UserProfileApiWrapperProps, State> {
  ref: HTMLElement | null = null
  height: number | undefined
  width: number | undefined

  componentDidMount() {
    loadMemberProfile(this.props.memberId, (user) => this.setState({ user }), this.props.liveData)
  }

  componentWillReceiveProps(props: UserProfileApiWrapperProps) {
    loadMemberProfile(props.memberId, (user) => this.setState({ user }), props.liveData)
  }

  render() {
    const { isReputationActive, isPMActive, liveData } = this.props
    const { user } = this.state
    const currentUserId = parseInt(window.inSidedData.user.userid)
    if (user) {
      return (
        <UserProfile
          profileRef={(el) => (this.ref = el)}
          user={user}
          currentUserId={currentUserId}
          isPMActive={isPMActive}
          isReputationActive={isReputationActive}
          disableTabs={liveData}
        />
      )
    }
    return (
      <div
        className='generic-loader'
        ref={(el) => (this.ref = el)}
        style={{
          color: '#ccc',
          marginTop: '-15px',
          marginBottom: '15px'
        }}
      />
    )
  }
}

interface ProfilePopupProps {
  liveData?: boolean
  memberId: number
  hoverControl?: HTMLElement
  parentEl?: HTMLElement
}

export default class ProfilePopup extends Component<ProfilePopupProps, State> {
  render() {
    const isReputationActive = globals('activeModules')?.includes('reputation')
    const isPMActive = globals('activeModules')?.includes('privateMessage')
    const element = this.props.hoverControl ? this.props.hoverControl : this.props.parentEl

    if (!element) return null

    return (
      <Popup
        hoverControl={element}
        size={{ width: 400, height: 60 }}
        triggerOnClick={this.props.liveData}
      >
        <UserProfileApiWrapper
          isReputationActive={isReputationActive}
          isPMActive={isPMActive}
          memberId={this.props.memberId}
          liveData={this.props.liveData}
        />
      </Popup>
    )
  }
}
